"use client";

import Link from "next/link";
import { useAuth } from "@/lib/auth";
import { usePathname, useRouter } from "next/navigation";
import { useMarketingJobsLinkVisible } from "@/hooks/useJobsNav";

export default function TopHeader({
  hideNav = false,
}: { hideNav?: boolean } = {}) {
  const { user, loading, signOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  // 高校生向けの案内ページと高校生区分では求人リンクを出さない（#1749）。
  // 案内ページは初回訪問時にまだ区分が確定していないため、パスでも判定する
  const jobsLinkVisible = useMarketingJobsLinkVisible();
  // トップページ・新卒向けページ・エンジニア向けページ・高校生向けページは同一のランディングを
  // 描画し、ヒーローコピーが二番目の見出しになる。これらではヘッダーのロゴを
  // 最上位見出しにして、各ページに最上位見出しがちょうど一つ存在する状態を保つ。
  const isMarketingLandingPage =
    pathname === "/" ||
    pathname === "/new-grad" ||
    pathname === "/engineer" ||
    pathname === "/high-school";
  const logoImage = (
    <img src="/logo_menren.svg" alt="MENREN" className="h-8 md:h-9" />
  );

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      router.push("/");
    }
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md shadow-sm">
      <div className="flex justify-between items-center h-16 px-4 md:px-12 max-w-7xl mx-auto">
        {isMarketingLandingPage ? (
          <h1 className="m-0 flex items-center">
            <Link href="/" className="flex items-center">
              {logoImage}
            </Link>
          </h1>
        ) : (
          <Link href="/" className="flex items-center">
            {logoImage}
          </Link>
        )}

        {!hideNav && (
          <nav className="hidden md:flex gap-8">
            <Link
              href="/guides"
              className="text-on-surface-variant hover:text-primary transition-colors font-medium text-sm"
            >
              ナレッジ記事
            </Link>
            <Link
              href="/career-center"
              className="text-on-surface-variant hover:text-primary transition-colors font-medium text-sm"
            >
              大学・キャリアセンターの方へ
            </Link>
            <Link
              href="/recruit"
              className="text-on-surface-variant hover:text-primary transition-colors font-medium text-sm"
            >
              企業の採用担当者の方へ
            </Link>
          </nav>
        )}

        <div className="flex items-center gap-3 md:gap-5">
          {!loading && !user && (
            <>
              <Link
                href="/login"
                className="text-on-surface-variant hover:text-primary transition-colors font-medium text-xs md:text-sm"
              >
                ログイン
              </Link>
              {jobsLinkVisible && (
                <Link
                  href="/jobs"
                  className="border-2 border-primary text-primary bg-white px-3 py-1.5 rounded-full font-bold text-xs md:text-sm hover:bg-primary hover:text-white transition-colors"
                >
                  求人を探す
                </Link>
              )}
              <Link
                href="/start"
                className="bg-accent text-on-secondary-fixed px-3 py-2 rounded-full font-bold text-xs md:text-sm hover:opacity-90 transition-opacity"
              >
                <span className="md:hidden">試してみる</span>
                <span className="hidden md:inline">今すぐ試してみる</span>
              </Link>
            </>
          )}
          {!loading && user && (
            <>
              <Link
                href="/practice/select"
                className="text-on-surface-variant hover:text-primary transition-colors font-medium text-xs"
              >
                練習する
              </Link>
              {jobsLinkVisible && (
                <Link
                  href="/jobs"
                  className="border-2 border-primary text-primary bg-white px-4 py-1.5 rounded-full font-bold text-xs hover:bg-primary hover:text-white transition-colors"
                >
                  求人を探す
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="text-on-surface-variant hover:text-primary transition-colors font-medium text-xs"
              >
                ログアウト
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
